import {EventStatus} from 'matrix-js-sdk';
import type {MatrixClient, MatrixEvent} from 'matrix-js-sdk';
import type {RoomMessageEventContent} from 'matrix-js-sdk/lib/@types/events';
import type {AiRequestContent} from './aiRequestProtocol';
import {record, requestReference} from './aiRequestProtocol';

type RequestClient = Pick<MatrixClient, 'getRoom' | 'getUserId' | 'sendMessage' | 'resendEvent'>;

const hasSamePayload = (event: MatrixEvent, expected: AiRequestContent): boolean => {
    const actual: unknown = event.getContent();
    if (!record(actual)) return false;
    const metadata = actual['org.phonehq.ai_request'];
    const identity = expected['org.phonehq.ai_request'];
    return record(metadata) && metadata.schema_version === identity.schema_version
        && metadata.request_id === identity.request_id && metadata.timezone === identity.timezone
        && metadata.retry_of === identity.retry_of && actual.msgtype === expected.msgtype
        && actual.audio_event_id === expected.audio_event_id && actual.time_range === expected.time_range
        && requestReference(actual) === requestReference(expected);
};

export const deliverAiRequest = async (client: RequestClient, roomId: string, content: AiRequestContent): Promise<string> => {
    const requestId = content['org.phonehq.ai_request'].request_id;
    const room = client.getRoom(roomId);
    const sameIdentity = (event: MatrixEvent): boolean => {
        const candidate: unknown = event.getContent()['org.phonehq.ai_request'];
        return record(candidate) && candidate.request_id === requestId;
    };
    const confirmed = room?.getLiveTimeline().getEvents().find(event =>
        event.getId()?.startsWith('$') && event.getSender() === client.getUserId() && sameIdentity(event));
    const confirmedId = confirmed?.getId();
    if (confirmed && confirmedId) {
        if (!hasSamePayload(confirmed, content)) throw new Error('AI request identity conflicts with existing content');
        return confirmedId;
    }
    const pending = room?.getEventForTxnId(requestId);
    if (pending && room) {
        if (!hasSamePayload(pending, content)) throw new Error('AI request identity conflicts with existing content');
        if (pending.status !== EventStatus.NOT_SENT) throw new Error('The existing request is still being sent');
        return (await client.resendEvent(pending, room)).event_id;
    }
    // The SDK's closed msgtype union omits custom Matrix message types.
    const response = await client.sendMessage(roomId, content as unknown as RoomMessageEventContent, requestId);
    return response.event_id;
};
